/**
 * The shared application root.
 *
 * Extracted from main.tsx so that both entry points render an identical tree:
 *
 *   main.tsx    order.app2food.com/olo/{storeId}   (the standalone product)
 *   embed.tsx   mounted on a client's own domain
 *
 * Anything added here reaches both. Provider order must not be changed casually —
 * OrderProvider reads from PaymentProvider, which reads from ShippingProvider.
 */

import React from "react";
import * as Sentry from "@sentry/react";
import { App } from "./App";

import "bootstrap/dist/css/bootstrap.min.css";
import "react-loading-skeleton/dist/skeleton.css";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import "./styles/loyalty-points.css";

import { Provider } from "react-redux";
import { store } from "./store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  ShippingProvider,
  PaymentProvider,
  OrderProvider,
  LoyaltyProvider,
  LayoutProvider,
  GiftcardProvider,
} from "./context";
import { ToastContainer } from "react-toastify";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getEmbedConfig } from "./embed/config";

const TOAST_AUTOCLOSE_TIME = import.meta.env.VITE_TOAST_AUTOCLOSE_TIME;
const GOOGLE_API_CLIENT_ID = import.meta.env.VITE_GOOGLE_API_CLIENT_ID;
const STRIPE_CLIENT_ID = import.meta.env.VITE_STRIPE_CLIENT_ID;
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

const stripePromise = loadStripe(STRIPE_CLIENT_ID);
const queryClient = new QueryClient({});

let sentryStarted = false;

/**
 * Idempotent, because the embed entry can be loaded once and mounted more than
 * once over a page's lifetime.
 */
export function initSentry(): void {
  if (sentryStarted || !SENTRY_DSN) return;
  sentryStarted = true;

  const config = getEmbedConfig();

  Sentry.init({
    dsn: SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [Sentry.replayIntegration()],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  // Without these, embedded errors are indistinguishable from standalone ones
  // and there is no way to tell which client domain a failure came from.
  Sentry.setTags({
    hosting_mode: config.mode,
    store_id: config.storeId,
    embed_host:
      typeof window !== "undefined" ? window.location.host : "unknown",
    router_mode: config.routerMode,
  });
}

export const AppRoot: React.FC = () => (
  <GoogleOAuthProvider clientId={GOOGLE_API_CLIENT_ID}>
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <Sentry.ErrorBoundary>
          <Provider store={store}>
            <LayoutProvider>
              <LoyaltyProvider>
                <ShippingProvider>
                  <PaymentProvider>
                    <GiftcardProvider>
                      <OrderProvider>
                        <Elements stripe={stripePromise}>
                          <App />
                        </Elements>
                        {/*
                          Renders in place rather than through a portal (checked
                          against the installed react-toastify), so it sits
                          inside the mount container and the prefix-wrapped CSS
                          scope reaches it. Same for react-select, which only
                          portals when given menuPortalTarget. react-bootstrap
                          Modal/Offcanvas/Overlay are the exceptions — handled by
                          embed/reactBootstrapScoped.tsx.
                        */}
                        <ToastContainer
                          position="top-right"
                          autoClose={TOAST_AUTOCLOSE_TIME}
                          hideProgressBar={true}
                          newestOnTop={true}
                          closeOnClick={true}
                          stacked
                          draggable
                          pauseOnHover
                        />
                      </OrderProvider>
                    </GiftcardProvider>
                  </PaymentProvider>
                </ShippingProvider>
              </LoyaltyProvider>
            </LayoutProvider>
          </Provider>
        </Sentry.ErrorBoundary>
      </QueryClientProvider>
    </React.StrictMode>
  </GoogleOAuthProvider>
);
